import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useCase, useCaseActions, useCases } from '../api/hooks'
import {
  Badge,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Section,
  formatAmount,
  formatDate,
} from '../components/ui'

export function CasesPage() {
  const [status, setStatus] = useState('')
  const query = useCases(status ? { status } : {})

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl">Vorgänge</h1>
        <div>
          <label className="sr-only" htmlFor="case-status">
            Status
          </label>
          <select
            id="case-status"
            className="input"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Alle</option>
            <option value="open">Offen</option>
            <option value="done">Erledigt</option>
          </select>
        </div>
      </div>

      {query.isPending ? (
        <LoadingBlock label="Vorgänge werden geladen" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title="Keine Vorgänge"
          description="Vorgänge entstehen automatisch, sobald zusammengehörende Dokumente eintreffen."
        />
      ) : (
        <ul className="space-y-2">
          {query.data.items.map((item) => (
            <li key={item.id} className="card">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/vorgaenge/$caseId"
                  params={{ caseId: item.id }}
                  className="min-w-0 flex-1 font-medium hover:underline"
                >
                  {item.title || 'Ohne Titel'}
                </Link>
                <Badge tone={item.status === 'open' ? 'accent' : 'ok'}>
                  {item.status === 'open' ? 'offen' : 'erledigt'}
                </Badge>
                {item.locked_by_user ? <Badge>manuell</Badge> : null}
              </div>
              <p className="mt-1 text-sm text-paper-500">
                {item.document_count} Dokumente · seit {formatDate(item.opened_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

export function CaseDetailPage() {
  const { caseId } = useParams({ from: '/vorgaenge/$caseId' })
  const query = useCase(caseId)
  const actions = useCaseActions(caseId)
  const allCases = useCases({ limit: 100 })
  const [mergeSource, setMergeSource] = useState('')

  if (query.isPending) return <LoadingBlock label="Vorgang wird geladen" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const detail = query.data
  const reasons = new Map(
    (detail.links as { document_id: string; reason: string; confidence: number }[]).map((link) => [
      link.document_id,
      link,
    ]),
  )

  return (
    <>
      <nav className="mb-4 text-sm">
        <Link to="/vorgaenge" className="text-paper-500 hover:underline">
          ← Vorgänge
        </Link>
      </nav>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl">{detail.title || 'Ohne Titel'}</h1>
          <p className="mt-1 text-sm text-paper-500">
            {detail.document_count} Dokumente · eröffnet {formatDate(detail.opened_at)}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => actions.setStatus.mutate(detail.status === 'open' ? 'done' : 'open')}
        >
          {detail.status === 'open' ? 'Als erledigt markieren' : 'Wieder öffnen'}
        </button>
      </header>

      <Section title="Zeitleiste">
        <ol className="space-y-3">
          {(detail.documents ?? []).map((document) => {
            const link = reasons.get(document.id)
            return (
              <li key={document.id} className="card">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/dokumente/$documentId"
                    params={{ documentId: document.id }}
                    className="min-w-0 flex-1 font-medium hover:underline"
                  >
                    {document.title || 'Ohne Betreff'}
                  </Link>
                  {document.doc_type ? <Badge tone="accent">{document.doc_type}</Badge> : null}
                  {link ? (
                    <span className="chip" title={`Konfidenz ${Math.round(link.confidence * 100)} %`}>
                      {link.reason}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-paper-500">
                  {formatDate(document.document_date ?? document.created_at)}
                  {document.amount_total !== null && document.amount_total !== undefined
                    ? ` · ${formatAmount(document.amount_total, document.currency)}`
                    : ''}
                </p>
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => actions.split.mutate(document.id)}
                    disabled={actions.split.isPending}
                  >
                    Aus Vorgang lösen
                  </button>
                </div>
              </li>
            )
          })}
        </ol>
      </Section>

      <Section title="Zusammenführen" description="Einen anderen Vorgang in diesen übernehmen.">
        <form
          className="card flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (mergeSource) actions.merge.mutate(mergeSource)
          }}
        >
          <div className="min-w-0 flex-1">
            <label className="label" htmlFor="merge-source">
              Quelle
            </label>
            <select
              id="merge-source"
              className="input"
              value={mergeSource}
              onChange={(event) => setMergeSource(event.target.value)}
            >
              <option value="">Bitte wählen</option>
              {(allCases.data?.items ?? [])
                .filter((item) => item.id !== caseId)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title || item.id.slice(0, 8)} ({item.document_count})
                  </option>
                ))}
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={!mergeSource || actions.merge.isPending}>
            Zusammenführen
          </button>
        </form>
      </Section>
    </>
  )
}
